import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { toast } from "sonner";
import {
  Users, DollarSign, FileText, MessageSquare, BarChart3,
  ArrowLeft, Trash2, Eye, Check, X, Plus, Edit
} from "lucide-react";

export default function AdminPanel() {
  const { user } = useAuth();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [demos, setDemos] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blogForm, setBlogForm] = useState({ title: "", excerpt: "", content: "", category: "", cover_image: "" });
  const [editingBlog, setEditingBlog] = useState(null);
  const [showBlogForm, setShowBlogForm] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, u, d, b] = await Promise.all([
        API.get("/admin/overview").catch(() => ({ data: null })),
        API.get("/admin/users").catch(() => ({ data: [] })),
        API.get("/admin/demo-requests").catch(() => ({ data: [] })),
        API.get("/blog").catch(() => ({ data: [] }))
      ]);
      setStats(s.data); setUsers(u.data); setDemos(d.data); setBlogs(b.data);
    } catch { toast.error("Failed to load some admin data"); }
    setLoading(false);
  };

  const updateDemoStatus = async (id, status) => {
    try {
      await API.put(`/admin/demo-requests/${id}/status`, { status });
      setDemos(prev => prev.map(d => d.id === id ? { ...d, status } : d));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const saveBlog = async () => {
    try {
      if (editingBlog) {
        await API.put(`/blog/${editingBlog}`, blogForm);
        toast.success("Post updated");
      } else {
        await API.post("/blog", blogForm);
        toast.success("Post created");
      }
      const { data } = await API.get("/blog");
      setBlogs(data);
      setShowBlogForm(false);
      setEditingBlog(null);
      setBlogForm({ title: "", excerpt: "", content: "", category: "", cover_image: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const deleteBlog = async (id) => {
    try {
      await API.delete(`/blog/${id}`);
      setBlogs(prev => prev.filter(b => b.id !== id));
      toast.success("Post deleted");
    } catch { toast.error("Delete failed"); }
  };

  const editBlog = (post) => {
    setBlogForm({ title: post.title, excerpt: post.excerpt, content: post.content, category: post.category, cover_image: post.cover_image || "" });
    setEditingBlog(post.id);
    setShowBlogForm(true);
  };

  if (user?.role !== "admin") return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0F0] flex items-center justify-center">
      <p>Access denied. Admin only.</p>
    </div>
  );

  const TABS = [
    { id: "stats", label: "Overview", icon: BarChart3 },
    { id: "users", label: "Users", icon: Users },
    { id: "demos", label: "Demo Requests", icon: MessageSquare },
    { id: "blog", label: "Blog", icon: FileText },
  ];

  const PRODUCT_LIST = [
    { id: "lead-scout", name: "Lead-Scout" },
    { id: "ghost-recovery", name: "Ghost-Recovery" },
    { id: "smart-sync", name: "Smart-Sync" },
    { id: "edge-guardian", name: "Edge Guardian" },
  ];

  const grantProduct = async (userId, productId) => {
    try {
      await API.post("/admin/grant-product", { user_id: userId, product_id: productId });
      toast.success("Product granted!");
      const { data } = await API.get("/admin/users");
      setUsers(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Grant failed"); }
  };

  const revokeProduct = async (userId, productId) => {
    if (!window.confirm(`Revoke ${productId} from this user?`)) return;
    try {
      await API.post("/admin/revoke-product", { user_id: userId, product_id: productId });
      toast.success("Product revoked");
      const { data } = await API.get("/admin/users");
      setUsers(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0F0]" data-testid="admin-page">
      <header className="bg-[#0A0A0A] border-b border-[#1E1E1E]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-heading text-xl font-black tracking-tighter uppercase">NRD<span className="text-[#00FF41]">.</span></Link>
            <span className="text-xs font-mono text-[#00FF41] border border-[#00FF41]/30 px-2 py-0.5">ADMIN</span>
          </div>
          <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#00FF41] transition-colors" data-testid="admin-back-dashboard">
            <ArrowLeft size={14} /> Dashboard
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-0 border border-[#1E1E1E] mb-8">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-r border-[#1E1E1E] last:border-r-0 ${tab === t.id ? 'bg-[#00FF41] text-[#0A0A0A]' : 'text-slate-400 hover:text-[#00FF41] hover:bg-[#111111]'}`}
                data-testid={`admin-tab-${t.id}`}>
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-6 h-6 border-2 border-[#00FF41] border-t-transparent animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats Tab */}
            {tab === "stats" && stats && (
              <div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[#1E1E1E] bg-[#111111]/80 backdrop-blur-md mb-8">
                  <div className="p-6 border-r border-[#1E1E1E] hover:bg-[#1a1a1a]/90 hover:-translate-y-1 hover:shadow-[0_0_15px_rgba(0,255,65,0.1)] transition-all duration-300">
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">Total Users</p>
                    <p className="font-heading text-3xl font-black" data-testid="admin-total-users">{stats.total_users}</p>
                  </div>
                  <div className="p-6 border-r border-[#1E1E1E] hover:bg-[#1a1a1a]/90 hover:-translate-y-1 hover:shadow-[0_0_15px_rgba(0,255,65,0.1)] transition-all duration-300">
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">Total Revenue</p>
                    <p className="font-heading text-3xl font-black text-[#00FF41]" data-testid="admin-total-revenue">${stats.total_revenue?.toFixed(0)}</p>
                  </div>
                  <div className="p-6 border-r border-[#1E1E1E] hover:bg-[#1a1a1a]/90 hover:-translate-y-1 hover:shadow-[0_0_15px_rgba(0,255,65,0.1)] transition-all duration-300">
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">Transactions</p>
                    <p className="font-heading text-3xl font-black">{stats.total_transactions}</p>
                  </div>
                  <div className="p-6 hover:bg-[#1a1a1a]/90 hover:-translate-y-1 hover:shadow-[0_0_15px_rgba(0,255,65,0.1)] transition-all duration-300">
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">New Demos</p>
                    <p className="font-heading text-3xl font-black text-[#00FF41]">{stats.new_demo_requests}</p>
                  </div>
                </div>
                <h3 className="font-heading text-lg font-medium tracking-tight mb-4">Product Breakdown</h3>
                <div className="grid md:grid-cols-2 gap-0 border border-[#1E1E1E] bg-[#111111]">
                  {stats.product_stats?.map((p, i) => (
                    <div key={p.id} className={`p-6 ${i % 2 === 0 ? 'border-r border-[#1E1E1E]' : ''} ${i < 2 ? 'border-b border-[#1E1E1E]' : ''}`}>
                      <p className="font-heading text-base font-medium">{p.name}</p>
                      <div className="flex items-center gap-6 mt-2">
                        <span className="text-xs text-slate-500">{p.buyers} buyers</span>
                        <span className="text-xs text-[#00FF41] font-mono">${p.revenue?.toFixed(0)} revenue</span>
                        <span className="text-xs text-slate-500">${p.price}/mo</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Users Tab */}
            {tab === "users" && (
              <div className="space-y-3">
                {users.map(u => (
                  <div key={u._id} className="bg-[#111111]/80 backdrop-blur-md border border-[#1E1E1E] p-5 hover:bg-[#151515] hover:border-[#00FF41]/30 hover:shadow-[0_0_20px_rgba(0,255,65,0.05)] transition-all duration-300 hover:translate-x-2" data-testid={`admin-user-${u._id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-medium">{u.name} <span className={`text-xs font-mono ml-2 px-2 py-0.5 ${u.role === 'admin' ? 'text-[#00FF41] bg-[#00FF41]/10 border border-[#00FF41]/30' : 'text-slate-400 bg-slate-800 border border-slate-700'}`}>{u.role}</span></p>
                        <p className="text-sm text-slate-400">{u.email}</p>
                        <p className="text-xs text-slate-600 mt-1">Joined: {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</p>
                      </div>
                    </div>
                    <div className="border-t border-[#1E1E1E] pt-3">
                      <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">Subscriptions</p>
                      <div className="flex flex-wrap gap-2">
                        {u.purchased_products?.length > 0 ? u.purchased_products.map(pid => (
                          <span key={pid} className="inline-flex items-center gap-1.5 text-xs bg-[#00FF41]/10 text-[#00FF41] border border-[#00FF41]/30 px-2 py-1">
                            {pid}
                            <button onClick={() => revokeProduct(u._id, pid)} className="hover:text-red-400 transition-colors" data-testid={`revoke-${u._id}-${pid}`}>
                              <X size={10} />
                            </button>
                          </span>
                        )) : <span className="text-xs text-slate-600">No subscriptions</span>}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {PRODUCT_LIST.filter(p => !u.purchased_products?.includes(p.id)).map(p => (
                          <button key={p.id} onClick={() => grantProduct(u._id, p.id)}
                            className="text-xs border border-[#1E1E1E] px-2 py-1 text-slate-500 hover:text-[#00FF41] hover:border-[#00FF41] transition-colors"
                            data-testid={`grant-${u._id}-${p.id}`}>
                            + {p.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Demo Requests Tab */}
            {tab === "demos" && (
              <div className="space-y-3">
                {demos.length === 0 ? <p className="text-sm text-slate-500">No demo requests yet.</p> : demos.map(d => (
                  <div key={d.id} className="bg-[#111111]/80 backdrop-blur-md border border-[#1E1E1E] p-5 hover:bg-[#151515] hover:border-[#00FF41]/30 hover:shadow-[0_0_20px_rgba(0,255,65,0.05)] transition-all duration-300 hover:translate-x-2" data-testid={`demo-request-${d.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{d.name} <span className="text-slate-500 text-sm">({d.email})</span></p>
                        {d.company && <p className="text-xs text-slate-500">{d.company}</p>}
                        <p className="text-xs text-[#00FF41] font-mono mt-1">Interest: {d.product_interest}</p>
                        {d.message && <p className="text-sm text-slate-400 mt-2">{d.message}</p>}
                        <p className="text-xs text-slate-600 mt-2">{new Date(d.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-mono px-2 py-0.5 ${d.status === 'new' ? 'text-[#00FF41] bg-[#00FF41]/10 border border-[#00FF41]/30' : d.status === 'contacted' ? 'text-blue-400 bg-blue-900/30 border border-blue-800' : 'text-slate-400 bg-slate-800 border border-slate-700'}`}>{d.status}</span>
                        {d.status === "new" && (
                          <button onClick={() => updateDemoStatus(d.id, "contacted")} className="text-xs border border-[#1E1E1E] px-2 py-1 text-slate-400 hover:text-[#00FF41] hover:border-[#00FF41] transition-colors" data-testid={`demo-mark-${d.id}`}>
                            <Check size={12} className="inline mr-1" /> Mark Contacted
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Blog Tab */}
            {tab === "blog" && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-medium">Blog Posts ({blogs.length})</h3>
                  <button onClick={() => { setShowBlogForm(true); setEditingBlog(null); setBlogForm({ title: "", excerpt: "", content: "", category: "", cover_image: "" }); }}
                    className="flex items-center gap-1.5 bg-[#00FF41] text-[#0A0A0A] px-4 py-2 text-xs font-bold hover:bg-[#00CC33] transition-colors"
                    data-testid="admin-new-blog-btn">
                    <Plus size={14} /> New Post
                  </button>
                </div>

                {showBlogForm && (
                  <div className="bg-[#111111] border border-[#1E1E1E] p-6 mb-6" data-testid="blog-form">
                    <h4 className="font-heading text-base font-medium mb-4">{editingBlog ? "Edit Post" : "New Post"}</h4>
                    <div className="space-y-3">
                      <input type="text" placeholder="Title" value={blogForm.title} onChange={e => setBlogForm(p => ({ ...p, title: e.target.value }))}
                        className="w-full bg-[#0A0A0A] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41]" data-testid="blog-form-title" />
                      <input type="text" placeholder="Category" value={blogForm.category} onChange={e => setBlogForm(p => ({ ...p, category: e.target.value }))}
                        className="w-full bg-[#0A0A0A] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41]" data-testid="blog-form-category" />
                      <input type="text" placeholder="Excerpt" value={blogForm.excerpt} onChange={e => setBlogForm(p => ({ ...p, excerpt: e.target.value }))}
                        className="w-full bg-[#0A0A0A] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41]" data-testid="blog-form-excerpt" />
                      <textarea rows={6} placeholder="Content" value={blogForm.content} onChange={e => setBlogForm(p => ({ ...p, content: e.target.value }))}
                        className="w-full bg-[#0A0A0A] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41] resize-none" data-testid="blog-form-content" />
                      <input type="url" placeholder="Cover Image URL (optional)" value={blogForm.cover_image} onChange={e => setBlogForm(p => ({ ...p, cover_image: e.target.value }))}
                        className="w-full bg-[#0A0A0A] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] placeholder-slate-600 focus:outline-none focus:border-[#00FF41]" data-testid="blog-form-image" />
                      <div className="flex gap-3">
                        <button onClick={() => { setShowBlogForm(false); setEditingBlog(null); }} className="flex-1 border border-[#1E1E1E] py-2.5 text-sm text-slate-400">Cancel</button>
                        <button onClick={saveBlog} className="flex-1 bg-[#00FF41] text-[#0A0A0A] py-2.5 text-sm font-bold hover:bg-[#00CC33]" data-testid="blog-form-save">{editingBlog ? "Update" : "Publish"}</button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {blogs.map(post => (
                    <div key={post.id} className="bg-[#111111]/80 backdrop-blur-md border border-[#1E1E1E] p-5 flex items-center justify-between hover:bg-[#151515] hover:border-[#00FF41]/30 hover:shadow-[0_0_20px_rgba(0,255,65,0.05)] transition-all duration-300 hover:translate-x-2" data-testid={`admin-blog-${post.id}`}>
                      <div>
                        <p className="font-medium">{post.title}</p>
                        <p className="text-xs text-[#00FF41] font-mono">{post.category} &middot; {post.author} &middot; {new Date(post.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link to={`/blog/${post.id}`} className="border border-[#1E1E1E] p-2 text-slate-400 hover:text-[#00FF41] hover:border-[#00FF41] transition-colors"><Eye size={14} /></Link>
                        <button onClick={() => editBlog(post)} className="border border-[#1E1E1E] p-2 text-slate-400 hover:text-[#00FF41] hover:border-[#00FF41] transition-colors" data-testid={`edit-blog-${post.id}`}><Edit size={14} /></button>
                        <button onClick={() => deleteBlog(post.id)} className="border border-[#1E1E1E] p-2 text-slate-400 hover:text-red-400 hover:border-red-800 transition-colors" data-testid={`delete-blog-${post.id}`}><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
